import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface LayoutState {
  /** The user's choice, persisted. */
  isSidebarCollapsed: boolean;
  /** Collapsed by a full-width page (chat, GenieArtifact) for as long as it is open; never persisted,
   *  so a reload from such a page does not leave the sidebar collapsed everywhere else. */
  isSidebarForced: boolean;
  isFullscreen: boolean;
  /** Set by a page that replaces the whole screen (e.g. the 403 page): AppLayout drops the header and sidebar. */
  isChromeHidden: boolean;
  /** DOM node in AppHeader where PageLayout portals the page title (null when the header isn't mounted). */
  pageHeaderSlot: HTMLElement | null;
  /** Number of mounted PageLayouts currently rendering into the slot; breadcrumbs show when 0. */
  pageHeaderCount: number;
}

interface LayoutActions {
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setSidebarForced: (forced: boolean) => void;
  toggleFullscreen: () => void;
  setFullscreen: (fullscreen: boolean) => void;
  exitFullscreen: () => void;
  setChromeHidden: (hidden: boolean) => void;
  setPageHeaderSlot: (el: HTMLElement | null) => void;
  registerPageHeader: () => () => void;
}

type LayoutStore = LayoutState & LayoutActions;

export const useLayoutStore = create<LayoutStore>()(
  persist(
    (set) => ({
      // State
      isSidebarCollapsed: false,
      isSidebarForced: false,
      isFullscreen: false,
      isChromeHidden: false,
      pageHeaderSlot: null,
      pageHeaderCount: 0,

      // Actions
      // On a full-width page, expanding only lifts the page's collapse; the user's choice stays.
      toggleSidebar: () =>
        set((state) =>
          state.isSidebarForced
            ? { isSidebarForced: false, isSidebarCollapsed: false }
            : { isSidebarCollapsed: !state.isSidebarCollapsed }
        ),
      
      setSidebarCollapsed: (collapsed: boolean) =>
        set({ isSidebarCollapsed: collapsed }),

      setSidebarForced: (forced: boolean) =>
        set({ isSidebarForced: forced }),

      toggleFullscreen: () =>
        set((state) => ({ isFullscreen: !state.isFullscreen })),
      
      setFullscreen: (fullscreen: boolean) =>
        set({ isFullscreen: fullscreen }),

      exitFullscreen: () =>
        set({ isFullscreen: false }),

      setChromeHidden: (hidden: boolean) =>
        set({ isChromeHidden: hidden }),

      setPageHeaderSlot: (el: HTMLElement | null) =>
        set({ pageHeaderSlot: el }),

      registerPageHeader: () => {
        set((state) => ({ pageHeaderCount: state.pageHeaderCount + 1 }));
        return () => set((state) => ({ pageHeaderCount: Math.max(0, state.pageHeaderCount - 1) }));
      },
    }),
    {
      name: 'layout-storage', // unique name for localStorage key
      partialize: (state) => ({ isSidebarCollapsed: state.isSidebarCollapsed }), // only persist sidebar state, not fullscreen
    }
  )
);

/** Whether the sidebar is shown collapsed: the user's choice or a full-width page's. */
export const useSidebarCollapsed = (): boolean =>
  useLayoutStore((s) => s.isSidebarCollapsed || s.isSidebarForced);
